import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'antd/es/form/Form';
import { trimValues } from '../../../utils/trimValues';
import { useNotificationContext } from '../../../contexts/NotificationContext';
import { useTranslation } from 'react-i18next';
import { localDataService } from '../../../services/localDataService';
import { useLocation, useNavigate } from 'react-router-dom';
import BasicCard from '../../../components/basicCard/BasicCard';
import { updateTask, updateTaskWithSync } from '../../../api/services/taskService';
import FuelPumpLayout from './FuelPumpLayout';
import FuelDispenserVerificationTable from './FuelDispenserVerificationTable';
import CollectionSessionInfoForm from './CollectionSessionInfoForm';
import SubmitFuelDispenserVerificationForm from './SubmitFuelDispenserVerification';
import Spin from 'antd/es/spin';
import dayjs from 'dayjs';
import FormModelComponent from '../../../components/model/FormModelComponent';
import { XSquare } from 'react-bootstrap-icons';
import { Input, Form, Steps } from 'antd';
import { REGEX_ALL_ALLOWED_SAFE_FILTER } from '../../../constants/regex';
import { TaskStatus } from '../../inspector/timelineItem';
import { useIsOnlineContext } from '../../../contexts/IsOnlineContext';
import { useUpdateTaskStatusToInProgress } from '../../../hooks/task/useUpdateTaskStatusToInProgress';

const initialCollectionSessionInfo = {
  inspector: [],
  typeOfPosition: '',
  establishment: '',
  startTime: dayjs,
  endTime: dayjs,
};

type IslandGrid = {
  islandId: string;
  islandNumber: number | null;
  cells: boolean[][];
  rows: number;
  cols: number;
};

export const FuelDispenserVerificationForm: React.FC = () => {
  const navigate = useNavigate();

  const [taskData, setTaskData] = useState<any>();

  const { t } = useTranslation('price');

  const [collectionSessionInfoDataForm] = useForm();
  const [respondentDataForm] = useForm();
  const [avenueAccessForm] = useForm();

  const [collectionSessionInfo, setCollectionSessionInfo] = useState(initialCollectionSessionInfo);
  const { successNotification, errorNotification } = useNotificationContext();
  const [triggeredFromTask, setTriggeredFromTask] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const respondentSectionRef = useRef<HTMLDivElement | null>(null);
  const [verificationTableData, setVerificationTableData] = useState({});
  const [islandLayout, setIslandLayout] = useState<(IslandGrid | null)[][]>([]);
  const [showModalStep, setShowModalStep] = useState<number>(0);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const locationRoute = useLocation();

  const handleCancel = () => {
    respondentDataForm.resetFields();
    collectionSessionInfoDataForm.resetFields();

    if (triggeredFromTask) {
      const activeTab = taskData?.triggeredFrom === 'inspections' ? '2' : '1';
      navigate('/inspector', { state: { activeTab } });
    }
  };

  const { updateTaskStatusToInProgress } = useUpdateTaskStatusToInProgress();

  useEffect(() => {
    const taskData = locationRoute.state;
    if (taskData) {
      setTriggeredFromTask(true);
      updateTaskStatusToInProgress(taskData).then((updated) => {
        setTaskData(updated || taskData);
      });
      taskData?.fuelStationName &&
        collectionSessionInfoDataForm.setFieldValue(
          'nameOfServiceStation',
          taskData?.fuelStationName
        );
    } else {
      setTaskData(undefined);
      setTriggeredFromTask(false);
    }
  }, [locationRoute.state, updateTaskStatusToInProgress]);

  const handleCreateFuelVerificationInspection = async () => {
    try {
      setIsLoading(true);
      const respondentFormData = respondentDataForm.getFieldsValue();
      const collectionSessionInfoValues = collectionSessionInfo;

      const trimmedRespondent = trimValues(respondentFormData);
      collectionSessionInfoDataForm.setFieldsValue(collectionSessionInfoValues);
      respondentDataForm.setFieldsValue(trimmedRespondent);

      const sessionData = {
        ...collectionSessionInfoValues,
        startTime: collectionSessionInfoValues?.startTime.valueOf(),
        endTime: collectionSessionInfoValues?.endTime.valueOf(),
      };

      await Promise.all([
        collectionSessionInfoDataForm.validateFields(),
        respondentDataForm.validateFields(),
      ]);

      const inspectionBody = {
        date: Date.now(),
        locationId: taskData?.locationId,
        establishmentId: taskData?.establishmentId,
        establishmentOther: taskData?.establishmentOther,
        respondent: null,
        signatureInitial: null,
        finalRemarks: null,
        reportedTime: Date.now(),
        taskRefId: taskData?.refId,
        fuelDispenserVerificationDetails: {
          ...sessionData,
          verificationTableData: verificationTableData,
          fuelPumpLayoutData: islandLayout,
          ...trimmedRespondent,
        },
      };

      try {
        const result = await localDataService.createLegalVerificationInspection(inspectionBody);

        if (result.inspection) {
          triggeredFromTask && updateTaskStatusToInProgress(taskData);
          successNotification(t('fuelDispenserVerificationSuccess'));
        }

        setCollectionSessionInfo(initialCollectionSessionInfo);

        collectionSessionInfoDataForm.resetFields();
        respondentDataForm.resetFields();
        handleCancel();
      } catch (error) {
        console.error('Failed to add scale verification:', error);
        errorNotification(`${t('fuelDispenserVerificationFailedAlert')}: ${error}`);
      } finally {
        setIsLoading(false);
      }
    } catch (validationError: any) {
      console.error('Form validation failed:', validationError.errorFields[0].errors[0]);
      errorNotification(`Form validation failed: ${validationError.errorFields[0].errors[0]}`);
      setIsLoading(false);
    }
  };

  const handleCollectionSessionInfoNext = async () => {
    const collectionSessionInfoFormData = collectionSessionInfoDataForm.getFieldsValue();
    const trimmedValues = trimValues(collectionSessionInfoFormData);
    collectionSessionInfoDataForm.setFieldsValue(trimmedValues);
    await collectionSessionInfoDataForm.validateFields();

    setCollectionSessionInfo({
      ...trimmedValues,
    });
    setShowModalStep(1);
  };

  const validateIslandLayout = (layout: (IslandGrid | null)[][]) => {
    if (checkDuplicateIslandNumbers(layout)) {
      errorNotification(`${t('islandNoDuplicatesNotAllowed')}`);
      throw new Error(`Island numbers are duplicated`);
    }

    for (let row = 0; row < layout.length; row++) {
      for (let col = 0; col < layout[row].length; col++) {
        const island = layout[row][col];

        if (!island) continue;

        if (
          island.islandNumber === null ||
          island.islandNumber === undefined ||
          Number.isNaN(island.islandNumber)
        ) {
          errorNotification(`${t('islandNumbersRequired')}`);
          throw new Error(
            `Island number is required for island at row ${row + 1}, column ${col + 1}`
          );
        }

        // Check if at least one cell is selected for the island
        const hasSelectedCell = island.cells.some((cellRow) =>
          cellRow.some((cellSelected) => cellSelected === true)
        );

        if (!hasSelectedCell) {
          errorNotification(`${t('islandCellsRequired')}`);
          throw new Error(
            `At least one cell must be selected for island ${island.islandNumber} at row ${row + 1}, column ${col + 1}`
          );
        }
      }
    }
  };

  const checkDuplicateIslandNumbers = (layout: (IslandGrid | null)[][]): boolean => {
    const seen = new Set<number>();

    for (const row of layout) {
      for (const island of row) {
        if (!island) continue;

        const { islandNumber } = island;

        if (islandNumber === null || islandNumber === undefined || Number.isNaN(islandNumber)) {
          continue;
        }
        if (seen.has(islandNumber)) {
          return true;
        }
        seen.add(islandNumber);
      }
    }
    return false; // no duplicates
  };
  const validateSecondStep = async (data: any) => {
    const avenueAccessData = await avenueAccessForm.getFieldsValue();
    const trimmedValues = trimValues(avenueAccessData);

    avenueAccessForm.setFieldsValue(trimmedValues);
    await avenueAccessForm.validateFields();
    setCollectionSessionInfo({
      ...collectionSessionInfo,
      ...trimmedValues,
    });

    // Validate island numbering
    validateIslandLayout(islandLayout);

    setVerificationTableData(data);
    setShowModalStep(2);
    setTimeout(() => {
      respondentSectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 0);
  };

  const goBack = () => {
    setShowModalStep(showModalStep != 0 ? showModalStep - 1 : 0);
  };

  const toggleCancelModal = () => {
    isCancelModalOpen ? setIsCancelModalOpen(false) : setIsCancelModalOpen(true);
  };

  return (
    <>
      <FormModelComponent
        isOpen={isCancelModalOpen}
        header={t('verificationCancelModalTitle')}
        content={t('verificationCancelModalConfirmaiton')}
        onClickedCancel={toggleCancelModal}
        footerAction={{
          confimation: {
            label: t('yes'),
            onClick: () => {
              handleCancel();
            },
          },
          cancel: {
            label: t('no'),
            onClick: toggleCancelModal,
          },
        }}
        closeIcon={<XSquare className="text-primary-font-red" size={20} />}
        contentStyles="mb-4 px-2 max-h-[80vh] overflow-y-auto space-y-2"
      ></FormModelComponent>

      <div className="my-6 w-full">
        <BasicCard>
          <div className="mt-4 flex w-full justify-center">
            <div className="mt-4 gap-2 text-center">
              <h1 className="mb-2 text-2xl font-bold">
                {t('fuelDispenserVerificationMainHeading')}
              </h1>
              <p className="mb-2">{t('fuelDispenserVerificationSubHeading')}</p>
              <p>{t('fuelDispenserVerificationSubHeading2')}</p>
            </div>
          </div>

          {isLoading ? (
            <div className="black absolute z-10 flex h-full w-[calc(100%-50px)] flex-row items-center justify-center rounded-lg bg-white/60">
              <Spin size={'large'} />
            </div>
          ) : null}
          <div className="m-0 flex items-center justify-center p-0">
            <div className="m-0 w-full p-0 pb-0 md:m-4">
              <div className="px-6 pb-5 pt-6 md:mb-4 md:mt-6 md:px-4">
                <Steps
                  size="small"
                  current={showModalStep}
                  labelPlacement="vertical"
                  items={[
                    { title: t('generalInformation') },
                    { title: t('fuelDispenserVerification') },
                    { title: t('signatureDetails') },
                  ]}
                  className="section-steps"
                />
              </div>

              {showModalStep == 0 && (
                <CollectionSessionInfoForm
                  form={collectionSessionInfoDataForm}
                  handleNext={handleCollectionSessionInfoNext}
                  handleCancel={toggleCancelModal}
                  isModal={false}
                  isCitizen={false}
                  triggeredFromTask={triggeredFromTask}
                  taskData={taskData}
                />
              )}

              {showModalStep == 1 && (
                <div className="mt-6 rounded-lg">
                  <Form form={avenueAccessForm} name="avenueAccessForm" layout="vertical">
                    <FuelPumpLayout onLayoutChange={setIslandLayout} initialLayout={islandLayout} />
                    <div className={'m-0 w-full p-2'}>
                      <Form.Item
                        label={t('avenueAccess')}
                        name="avenueAccess"
                        rules={[
                          { required: true, message: t('avenueAccessRequired') },
                          {
                            validator: (_, value) => {
                              if (value && value.trim() === '') {
                                return Promise.reject(t('avenueAccessSpaceValidation'));
                              }
                              return Promise.resolve();
                            },
                          },
                          {
                            validator: (_, value) => {
                              if (!value) return Promise.resolve();
                              // Allow letters, numbers, space, punctuation and special characters
                              // const regex = /^[A-Za-z0-9ÁÉÍÓÚáéíóúÑñÜü\s,.\-!?;:()"'&@#$%^*+=/]+$/;
                              const regex = REGEX_ALL_ALLOWED_SAFE_FILTER;
                              return regex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(t('invalidInput'));
                            },
                          },
                          {
                            validator: (_, value) => {
                              if (!value || value.trim() === '') {
                                return Promise.resolve();
                              }

                              const trimmedLength = value.replace(/\s/g, '').length;
                              if (trimmedLength < 3) {
                                return Promise.reject(t('avenueAccessMinValidation'));
                              }
                              return Promise.resolve();
                            },
                          },
                          {
                            validator: (_, value) => {
                              if (!value || value.trim() === '') {
                                return Promise.resolve();
                              }

                              const trimmedLength = value.replace(/\s/g, '').length;
                              if (trimmedLength > 200) {
                                return Promise.reject(t('avenueAccessMaxValidation'));
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <Input.TextArea placeholder={t('avenueAccessPlaceholder')} />
                      </Form.Item>
                      <span className="p-4"></span>
                      <FuelDispenserVerificationTable
                        submit={validateSecondStep}
                        initialValues={verificationTableData}
                        handleBack={goBack}
                        handleCancel={toggleCancelModal}
                        onValidationError={errorNotification}
                      />
                    </div>
                  </Form>
                </div>
              )}

              {showModalStep == 2 && (
                <div ref={respondentSectionRef}>
                  <SubmitFuelDispenserVerificationForm
                    form={respondentDataForm}
                    handleSubmit={handleCreateFuelVerificationInspection}
                    handleCancel={toggleCancelModal}
                    handleBack={goBack}
                    disabled={isLoading}
                  ></SubmitFuelDispenserVerificationForm>
                </div>
              )}
            </div>
          </div>
        </BasicCard>
      </div>
    </>
  );
};

export default FuelDispenserVerificationForm;
